import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LoadingOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import Modal from '@common/Modal'
import { connect, useDispatch, useSelector } from 'react-redux'
import { hide } from '@store/confirm-reducer'
import { selectProfile } from '@store/profile.reducer'
import { AuthService, idUserAuthorizedOnTheAuthorizationPage } from '@/services/AuthService'
import { useAccountActions } from '@/hooks/useAccountActions'
import Row from '@UI/Row'
import s from './index.module.css'
import headerClasses from '@common/Header/index.module.css'
import Button from '@UI/Button'
import NewButton from '@UI/New/Button'
import Preloader from '@UI/Preloader'

const CODE_LENGTH = 4

const TwoFactorContent = ({ data, code, setCode, error, setError, disabled = false }) => {
  const refs = useRef([])
  const codeLength = data.codeLength || CODE_LENGTH

  useEffect(() => {
    refs.current[0]?.focus()
  }, [])

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const nextCode = [...code]
    nextCode[index] = digit
    setCode(nextCode)
    setError('')

    if (digit && index < codeLength - 1) {
      refs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (event) => {
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, codeLength)

    if (!pasted) return

    event.preventDefault()
    setCode(Array.from({ length: codeLength }, (_, index) => pasted[index] ?? ''))
    setError('')
    refs.current[Math.min(pasted.length, codeLength) - 1]?.focus()
  }

  return (
    <div className={s.twoFactorConfirm}>
      <p className={s.twoFactorConfirmText}>
        {data.description || 'Введите 4 цифры из кода, отправленного в мессенджер Max.'}
      </p>
      {data.descriptionLink && (
        <a
          href={data.descriptionLink}
          className={s.twoFactorConfirmLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          Открыть MAX-бота
        </a>
      )}
      <div className={s.twoFactorCode}>
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(node) => {
              refs.current[index] = node
            }}
            className={s.twoFactorCodeInput}
            inputMode="numeric"
            maxLength={1}
            disabled={disabled}
            value={digit}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
          />
        ))}
      </div>
      {error && <div className={s.twoFactorError}>{error}</div>}
    </div>
  )
}

const Confirm = ({ data }) => {
  const dispatch = useDispatch()
  const profile = useSelector(selectProfile)
  const { loadingAccounts, disconnectAccount, onLogin } = useAccountActions()
  const codeLength = data.codeLength || CODE_LENGTH
  const [code, setCode] = useState(Array(codeLength).fill(''))
  const [error, setError] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)
  const isTwoFactor = data.variant === 'twoFactor'
  const isDepartmentDelete = data.variant === 'departmentDelete'
  const isTwoFactorBusy = isTwoFactor && data.loading
  const isDepartmentDeleteBusy = isDepartmentDelete && (data.loading || isConfirming)
  const showLoadingOverlay = isTwoFactor && data.loading
  const isBlocking = Boolean(data.blocking)
  const showLogoutButton = data.show && isBlocking

  useEffect(() => {
    if (data.show) {
      setCode(Array(codeLength).fill(''))
      setError('')
      setIsConfirming(false)
    }
  }, [data.show, codeLength])

  useEffect(() => {
    if (!data.resetCodeKey) return

    setCode(Array(codeLength).fill(''))
    setError('')
    setIsConfirming(false)
  }, [data.resetCodeKey, codeLength])

  const handleLogout = async () => {
    if (loadingAccounts?.length) return

    const authorizedParentId = localStorage.getItem(
      idUserAuthorizedOnTheAuthorizationPage,
    )

    if (authorizedParentId && authorizedParentId !== String(profile?.id)) {
      await disconnectAccount(profile?.id)
      onLogin(authorizedParentId)
    } else {
      AuthService.logout()
    }
  }

  const onConfirm = async () => {
    const confirmationCode = code.join('')

    if (isTwoFactor) {
      if (confirmationCode.length !== codeLength) {
        setError(`Введите код из ${codeLength} цифр`)
        return
      }

      if (data.expectedCode && confirmationCode !== data.expectedCode) {
        setError('Неверный код подтверждения')
        return
      }
    }

    setIsConfirming(true)

    try {
      const result = await data.onConfirm({
        code: confirmationCode,
        setError,
      })

      if (result === false) return

      dispatch(hide())
    } catch (error) {
      setError(error?.message || 'Не удалось подтвердить код')
    } finally {
      setIsConfirming(false)
    }
  }

  const onCancel = () => {
    if (isTwoFactorBusy || isBlocking) return

    data.onCancel()
    dispatch(hide())
  }

  const content = (
    <>
      {showLogoutButton && (
        <button
          type="button"
          className={classNames(
            headerClasses.button,
            s.logoutButton,
            'trackClickWhilePerformingWork',
          )}
          title="Выйти"
          onClick={handleLogout}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 7.13193V6.61204C7 4.46614 7 3.3932 7.6896 2.79511C8.37919 2.19703 9.44136 2.34877 11.5657 2.65224L15.8485 3.26408C18.3047 3.61495 19.5327 3.79039 20.2664 4.63628C21 5.48217 21 6.72271 21 9.20377V14.7962C21 17.2773 21 18.5178 20.2664 19.3637C19.5327 20.2096 18.3047 20.385 15.8485 20.7359L11.5657 21.3478C9.44136 21.6512 8.37919 21.803 7.6896 21.2049C7 20.6068 7 19.5339 7 17.388V17.066"
              stroke="var(--color-text-main)"
              strokeWidth="1.5"
            />
            <path
              d="M16 12L16.5857 11.5315L16.9605 12L16.5857 12.4685L16 12ZM4 12.75C3.58579 12.75 3.25 12.4142 3.25 12C3.25 11.5858 3.58579 11.25 4 11.25V12.75ZM12.5857 6.53148L16.5857 11.5315L15.4143 12.4685L11.4143 7.46852L12.5857 6.53148ZM16.5857 12.4685L12.5857 17.4685L11.4143 16.5315L15.4143 11.5315L16.5857 12.4685ZM16 12.75H4V11.25H16V12.75Z"
              fill="var(--color-text-main)"
            />
          </svg>
        </button>
      )}
      <Modal
        className={classNames(s.confirm, {
          [s.twoFactorModal]: isTwoFactor,
          [s.departmentDeleteModal]: isDepartmentDelete,
        })}
        active={data.show}
        setActive={onCancel}
        disableBackdropDismiss={isBlocking}
        style={{ zIndex: 100000 }}
      >
        <div className={showLoadingOverlay ? s.loadingContent : ''}>
          <h2 className={s.title}>{data.title}</h2>
          {isTwoFactor ? (
            <TwoFactorContent
              data={data}
              code={code}
              setCode={setCode}
              error={error}
              setError={setError}
              disabled={isTwoFactorBusy}
            />
          ) : (
            <p>{data.text}</p>
          )}

          {isTwoFactor ? (
            <div className={`${s.twoFactorButtons} ${data.hideCancel ? s.twoFactorButtonsSingle : ''}`}>
              <button
                type="button"
                className={s.twoFactorPrimaryButton}
                onClick={onConfirm}
                disabled={isTwoFactorBusy}
              >
                {isConfirming ? <LoadingOutlined spin className={s.buttonSpinner} /> : data.confirmText || 'Подтвердить'}
              </button>
              {!data.hideCancel && (
                <button
                  type="button"
                  className={s.twoFactorSecondaryButton}
                  onClick={onCancel}
                  disabled={isTwoFactorBusy}
                >
                  {data.cancelText || 'Отменить'}
                </button>
              )}
            </div>
          ) : isDepartmentDelete ? (
            <div className={s.departmentDeleteButtons}>
              <NewButton
                color="gray"
                onClick={onCancel}
                disabled={isDepartmentDeleteBusy}
              >
                {data.cancelText || 'Отмена'}
              </NewButton>
              <NewButton
                color="danger"
                onClick={onConfirm}
                disabled={isDepartmentDeleteBusy}
                isLoading={isDepartmentDeleteBusy}
              >
                {data.confirmText || 'Удалить'}
              </NewButton>
            </div>
          ) : (
            <Row className={s.buttonRow}>
              <Button
                styles={'yellow'}
                onClick={onConfirm}
                loading={data.loading}
                disable={data.loading}
              >
                {data.confirmText || 'Да'}
              </Button>
              <Button onClick={onCancel}>{data.cancelText || 'Отмена'}</Button>
            </Row>
          )}
        </div>
        {showLoadingOverlay && (
          <div className={s.loadingOverlay}>
            <Preloader />
          </div>
        )}
      </Modal>
    </>
  )

  if (typeof document === 'undefined') {
    return content
  }

  return createPortal(content, document.body)
}

export default connect((state) => ({
  data: state.confirm,
}), {})(Confirm)
